import { motion } from 'framer-motion';
import { Calendar, Eye, FileText, PlusCircle, RefreshCw, XCircle } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import { typography } from '../../styles/typography';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input.js';
import { taxApi } from './FdocApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const TaxOverView = () => {
  // Log screen view
  useScreenLogging('TaxOverView', {
    component: 'TaxOverView',
  });
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedDocType, setSelectedDocType] = useState('all');
  
  // Get overlay functions from context
  const { openOverlay } = useOverlay();

  // Create empty row function
  const createEmptyRow = () => ({
    date: '',
    docNumber: '',
    totalAmount: '',
    taxAmount: '',
    vendorPan: '',
    vendorGstin: '',
    customerPan: '',
    customerGstin: '',
    docUrl: '', // Will be used for preview later
  });

  // Table rows state
  const [tableRows, setTableRows] = useState([createEmptyRow()]);
  
  // Track if data is from API (read-only)
  const [isDataFromAPI, setIsDataFromAPI] = useState(false);
  
  // Errors state
  const [errors, setErrors] = useState({
    rows: [{}],
  });

  // Loading and success/error states
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  
  // Tax data from API
  const [taxData, setTaxData] = useState(null);
  const [taxSummary, setTaxSummary] = useState(null);

  // Track initial mount to fetch data on first load
  const isInitialMount = useRef(true);

  // Document type options (mapped to API values)
  const docTypeOptions = [
    { value: 'all', label: 'All Documents' },
    { value: 'quotation', label: 'Quotation' },
    { value: 'po', label: 'PO' },
    { value: 'invoice', label: 'Invoice' },
    { value: 'bill', label: 'Bill' },
  ];

  // Handle row change
  const handleRowChange = (index, field, value) => {
    setTableRows((prevRows) => {
      const nextRows = [...prevRows];
      const updatedRow = { ...nextRows[index], [field]: value };
      nextRows[index] = updatedRow;
      return nextRows;
    });

    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.map((err, idx) =>
        idx === index ? { ...err, [field]: '' } : err
      ),
    }));
  };

  // Handle add row
  const handleAddRow = () => {
    setTableRows((prev) => [...prev, createEmptyRow()]);
    setErrors((prev) => ({
      ...prev,
      rows: [...prev.rows, {}],
    }));
  };

  // Handle clear row
  const handleClearRow = (index) => {
    setTableRows((prev) =>
      prev.map((row, idx) => (idx === index ? createEmptyRow() : row))
    );
    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.map((rowError, idx) =>
        idx === index ? {} : rowError
      ),
    }));
  };

  // Handle remove row
  const handleRemoveRow = (index) => {
    if (tableRows.length === 1) {
      return;
    }
    setTableRows((prev) => prev.filter((_, idx) => idx !== index));
    setErrors((prev) => ({
      ...prev,
      rows: prev.rows.filter((_, idx) => idx !== index),
    }));
  };

  // Handle view document (preview functionality)
  const handleViewDoc = (docUrl, docNumber) => {
    if (!docUrl) {
      setError('Document URL is not available');
      return;
    }
    
    // Open the tax preview overlay
    openOverlay('taxPreview', {
      documentUrl: docUrl,
      documentNumber: docNumber || 'Document'
    });
  };

  // Fetch tax data from API
  const fetchTaxData = async () => {
    if (!selectedMonth) {
      setError('Please select a month');
      return;
    }

    setIsFetchingData(true);
    setError('');
    setTaxData(null);
    setTaxSummary(null);

    try {
      // Parse month and year from selectedMonth (format: YYYY-MM)
      const [year, month] = selectedMonth.split('-').map(Number);

      // Build API parameters
      const params = {
        month,
        year,
        limit: 200,
      };

      // Add document type filter if selected (skip if 'all')
      if (selectedDocType && selectedDocType !== 'all') {
        params.document_type = selectedDocType;
      }

      console.log('Fetching tax data with params:', params);

      // Fetch tax overview data
      const data = await taxApi.getOverview(params);

      console.log('Tax data received:', data);
      console.log('Data structure:', {
        hasItems: !!data.items,
        itemsLength: data.items?.length,
        hasDocuments: !!data.documents,
        documentsLength: data.documents?.length,
        dataKeys: Object.keys(data)
      });

      setTaxData(data);
      setTaxSummary(data.summary);

      // Log successful API call
      loggingService.logInfo(
        'taxFetchOverview',
        'Successfully fetched tax overview data',
        {
          endpoint: '/tax/overview',
          method: 'GET',
          month,
          year,
          documentType: selectedDocType,
          documentCount: data.count || 0,
          itemsCount: (data.items || data.documents || []).length,
        }
      ).catch(logError => {
        console.warn('[TaxOverView] Failed to log event:', logError);
      });

      // Populate table rows with fetched data
      const itemsArray = data.items || data.documents || [];
      console.log('Items array to process:', itemsArray);
      
      if (itemsArray && itemsArray.length > 0) {
        console.log(`Found ${itemsArray.length} items in API response`);
        
        const newRows = itemsArray.map((doc, idx) => {
          console.log(`Processing item ${idx + 1}:`, doc);
          
          // Helper function to format date from ISO string to YYYY-MM-DD
          const formatDate = (dateValue) => {
            if (!dateValue) return '';
            // If it's already in YYYY-MM-DD format, return as is
            if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
              return dateValue;
            }
            // If it's an ISO string, convert to YYYY-MM-DD
            try {
              const date = new Date(dateValue);
              if (!isNaN(date.getTime())) {
                return date.toISOString().split('T')[0];
              }
            } catch (e) {
              console.warn('Invalid date format:', dateValue);
            }
            return '';
          };

          return {
            // Date: handle both ISO string and YYYY-MM-DD format
            date: formatDate(doc.date),
            // Doc number: check both direct field and API field
            docNumber: doc.docNumber || doc.document_number || '',
            // Amounts: check both direct fields and API fields, convert to string
            totalAmount: String(doc.totalAmount || doc.total_amount || ''),
            taxAmount: String(doc.taxAmount || doc.tax_amount || ''),
            // Vendor fields: check direct fields first, then map based on other_party_type
            vendorPan: doc.vendorPan || (doc.other_party_type === 'vendor' ? (doc.other_party_pan || '') : (doc.our_pan || '')),
            vendorGstin: doc.vendorGstin || (doc.other_party_type === 'vendor' ? (doc.other_party_gstin || '') : (doc.our_gstin || '')),
            // Customer fields: check direct fields first, then map based on other_party_type
            customerPan: doc.customerPan || (doc.other_party_type === 'customer' ? (doc.other_party_pan || '') : (doc.our_pan || '')),
            customerGstin: doc.customerGstin || (doc.other_party_type === 'customer' ? (doc.other_party_gstin || '') : (doc.our_gstin || '')),
            // Document URL: check both direct field and API field
            docUrl: doc.docUrl || doc.document_url || '',
          };
        });
        
        console.log(`Created ${newRows.length} table rows:`, newRows);
        setTableRows(newRows);
        setErrors({ rows: newRows.map(() => ({})) });
        setIsDataFromAPI(true);
      } else {
        console.log('No items found in API response');
        // If no documents, keep empty row
        setTableRows([createEmptyRow()]);
        setErrors({ rows: [{}] });
        setIsDataFromAPI(false);
      }

      setSuccess(`Successfully loaded ${data.count || 0} document(s)`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error fetching tax data:', err);
      setError(err.message || 'Failed to fetch tax data. Please try again.');
    } finally {
      setIsFetchingData(false);
    }
  };

  // Handle mobile view detection
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');

    const updateView = (event) => {
      setIsMobileView(event.matches);
    };

    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  // Automatically fetch data when component mounts
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (selectedMonth) {
        fetchTaxData();
      }
    }
  }, [selectedMonth]);

  // Validate form
  const validateForm = () => {
    const rowErrors = tableRows.map((row) => {
      const rowError = {};

      if (!row.date) {
        rowError.date = 'Date is required';
      }

      if (!row.docNumber || !row.docNumber.trim()) {
        rowError.docNumber = 'Doc number is required';
      }

      if (!row.totalAmount) {
        rowError.totalAmount = 'Total amount is required';
      } else if (parseFloat(row.totalAmount) <= 0) {
        rowError.totalAmount = 'Total amount must be greater than 0';
      }

      if (!row.taxAmount) {
        rowError.taxAmount = 'Tax amount is required';
      } else if (parseFloat(row.taxAmount) < 0) {
        rowError.taxAmount = 'Tax amount cannot be negative';
      }

      if (!row.vendorPan || !row.vendorPan.trim()) {
        rowError.vendorPan = 'Vendor PAN is required';
      } else if (row.vendorPan.length !== 10) {
        rowError.vendorPan = 'Vendor PAN must be 10 characters';
      }

      if (!row.vendorGstin || !row.vendorGstin.trim()) {
        rowError.vendorGstin = 'Vendor GSTIN is required';
      } else if (row.vendorGstin.length !== 15) {
        rowError.vendorGstin = 'Vendor GSTIN must be 15 characters';
      }

      if (!row.customerPan || !row.customerPan.trim()) {
        rowError.customerPan = 'Customer PAN is required';
      } else if (row.customerPan.length !== 10) {
        rowError.customerPan = 'Customer PAN must be 10 characters';
      }

      if (!row.customerGstin || !row.customerGstin.trim()) {
        rowError.customerGstin = 'Customer GSTIN is required';
      } else if (row.customerGstin.length !== 15) {
        rowError.customerGstin = 'Customer GSTIN must be 15 characters';
      }

      return rowError;
    });

    const hasRowErrors = rowErrors.some(
      (rowError) => Object.keys(rowError).length > 0
    );

    setErrors({
      rows: rowErrors,
    });

    return !hasRowErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setError('Please fill in all required fields correctly');
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // TODO: Replace with actual API call
      console.log('Submitting tax data:', {
        month: selectedMonth,
        docType: selectedDocType,
        rows: tableRows,
      });

      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setSuccess('Tax data submitted successfully!');
      
      // Optionally reset form after successful submission
      // setTableRows([createEmptyRow()]);
      // setSelectedMonth(getCurrentMonth());
      // setSelectedDocType('');
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.message || 'Failed to submit tax data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  // Debug: Log table rows on render
  console.log('Rendering with tableRows:', tableRows.length, 'rows');

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <form onSubmit={handleSubmit} className="h-full">
          <motion.div
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            className="space-y-8"
          >
          {/* Header */}
          <div className="mb-8">
            <h1 className={typography.h2} style={{ color: '#000000' }}>
              Tax Overview
            </h1>
            <p className="mt-2 text-gray-600">
              View and manage tax documents
            </p>
          </div>

          {/* Dropdowns Section */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
            {/* Select Month Dropdown */}
            <div className="min-w-[200px]">
              <label
                htmlFor="tax-month"
                className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
              >
                <Calendar className="w-4 h-4" />
                Select Month
              </label>
              <input
                id="tax-month"
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              />
            </div>

            {/* Document Type Dropdown */}
            <div className="min-w-[200px]">
              <label
                htmlFor="tax-doc-type"
                className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700"
              >
                <FileText className="w-4 h-4" />
                Document Type
              </label>
              <select
                id="tax-doc-type"
                value={selectedDocType}
                onChange={(e) => setSelectedDocType(e.target.value)}
                className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
              >
                {docTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Fetch Data Button */}
            <div className="min-w-[200px]">
              <button
                type="button"
                onClick={fetchTaxData}
                disabled={!selectedMonth || isFetchingData}
                className="w-full min-h-[48px] rounded-2xl border border-green-500 bg-green-600 text-white px-4 py-2 text-sm font-semibold hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center justify-center gap-2"
              >
                {isFetchingData ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Fetching...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Fetch Data
                  </>
                )}
              </button>
            </div>
          </div>


          {/* Table Section */}
          <div className="rounded-3xl">
            <div className="relative group">
              {!isMobileView && (
                <>
                  <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                    <table className="min-w-full border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">
                        Date
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">
                        Doc# Number
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">
                        Total Amount
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">
                        Tax Amount
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">
                        View Doc
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">
                        Vendor PAN
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">
                        Vendor GSTIN
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[120px]">
                        Customer PAN
                      </th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">
                        Customer GSTIN
                      </th>
                      <th className="px-4 py-3 font-semibold text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, index) => (
                      <tr
                        key={`tax-row-${index}`}
                        className="border-t border-gray-100"
                      >
                        <td className="px-4 py-4 align-top text-gray-800">
                          {index + 1}
                        </td>
                         <td className="px-1 py-1">
                          <input
                            type="date"
                            value={row.date}
                            onChange={(e) =>
                              handleRowChange(index, 'date', e.target.value)
                            }
                            readOnly={isDataFromAPI}
                            className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 ${
                              errors.rows[index]?.date
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.date && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].date}
                            </p>
                          )}
                        </td>
                         <td className="px-1 py-1 min-w-[140px]">
                          <Input
                            type="text"
                            value={row.docNumber}
                            onChange={(e) =>
                              handleRowChange(
                                index,
                                'docNumber',
                                e.target.value
                              )
                            }
                            readOnly={isDataFromAPI}
                            placeholder="Enter doc number"
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                              errors.rows[index]?.docNumber
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.docNumber && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].docNumber}
                            </p>
                          )}
                        </td>
                         <td className="px-1 py-1 min-w-[120px]">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.totalAmount}
                            onChange={(e) =>
                              handleRowChange(
                                index,
                                'totalAmount',
                                e.target.value
                              )
                            }
                            readOnly={isDataFromAPI}
                            placeholder="0.00"
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                              errors.rows[index]?.totalAmount
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.totalAmount && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].totalAmount}
                            </p>
                          )}
                        </td>
                        <td className="px-1 py-1 min-w-[120px]">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.taxAmount}
                            onChange={(e) =>
                              handleRowChange(index, 'taxAmount', e.target.value)
                            }
                            readOnly={isDataFromAPI}
                            placeholder="0.00"
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                              errors.rows[index]?.taxAmount
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.taxAmount && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].taxAmount}
                            </p>
                          )}
                        </td>
                        <td className="px-1 py-1 min-w-[140px]">
                          <button
                            onClick={() =>
                              handleViewDoc(row.docUrl, row.docNumber)
                            }
                            disabled={!row.docUrl}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </td>
                         <td className="px-1 py-1 min-w-[120px]">
                          <Input
                            type="text"
                            value={row.vendorPan}
                            onChange={(e) =>
                              handleRowChange(index, 'vendorPan', e.target.value)
                            }
                            readOnly={isDataFromAPI}
                            placeholder="Enter Vendor PAN"
                            maxLength={10}
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                              errors.rows[index]?.vendorPan
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.vendorPan && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].vendorPan}
                            </p>
                          )}
                        </td>
                        <td className="px-1 py-1 min-w-[140px]">
                          <Input
                            type="text"
                            value={row.vendorGstin}
                            onChange={(e) =>
                              handleRowChange(index, 'vendorGstin', e.target.value)
                            }
                            readOnly={isDataFromAPI}
                            placeholder="Enter Vendor GSTIN"
                            maxLength={15}
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                              errors.rows[index]?.vendorGstin
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.vendorGstin && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].vendorGstin}
                            </p>
                          )}
                        </td>
                        <td className="px-1 py-1 min-w-[120px]">
                          <Input
                            type="text"
                            value={row.customerPan}
                            onChange={(e) =>
                              handleRowChange(index, 'customerPan', e.target.value)
                            }
                            readOnly={isDataFromAPI}
                            placeholder="Enter Customer PAN"
                            maxLength={10}
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                              errors.rows[index]?.customerPan
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.customerPan && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].customerPan}
                            </p>
                          )}
                        </td>
                        <td className="px-1 py-1 min-w-[140px]">
                          <Input
                            type="text"
                            value={row.customerGstin}
                            onChange={(e) =>
                              handleRowChange(index, 'customerGstin', e.target.value)
                            }
                            readOnly={isDataFromAPI}
                            placeholder="Enter Customer GSTIN"
                            maxLength={15}
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                              errors.rows[index]?.customerGstin
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.customerGstin && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].customerGstin}
                            </p>
                          )}
                        </td>
                         <td className="px-1 py-1 text-right">
                          {!isDataFromAPI && (
                            <button
                              type="button"
                              onClick={() =>
                                index === 0
                                  ? handleClearRow(index)
                                  : handleRemoveRow(index)
                              }
                              disabled={index !== 0 && tableRows.length === 1}
                              className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                index === 0
                                  ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                                  : tableRows.length === 1
                                    ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                    : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                              }`}
                              aria-label={
                                index === 0
                                  ? 'Clear row values'
                                  : 'Remove item row'
                              }
                            >
                              <XCircle className="h-4 w-4" />
                              {index === 0 ? 'Clear' : 'Close'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                    </table>
                  </div>

                  {!isDataFromAPI && (
                    <div className="pointer-events-none mt-4 flex justify-center">
                      <button
                        type="button"
                        onClick={handleAddRow}
                        className="pointer-events-auto inline-flex translate-y-2 items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm opacity-0 transition-all duration-200 group-hover:translate-y-0 group-hover:opacity-100 focus-visible:translate-y-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                      >
                        <PlusCircle className="h-4 w-4" />
                        Add Row
                      </button>
                    </div>
                  )}
                </>
              )}

              {isMobileView && (
                <div className="space-y-4 rounded-3xl border border-gray-200 bg-white p-4 shadow-sm">
                  {tableRows.map((row, index) => (
                    <div
                      key={`tax-card-${index}`}
                      className="rounded-2xl border border-gray-100 p-4"
                    >
                       <div className="mb-3 flex items-center justify-between">
                        <p className="text-sm font-semibold text-gray-700">
                          Row #{index + 1}
                        </p>
                        {!isDataFromAPI && (
                          <button
                            type="button"
                            onClick={() =>
                              index === 0
                                ? handleClearRow(index)
                                : handleRemoveRow(index)
                            }
                            disabled={index !== 0 && tableRows.length === 1}
                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                              index === 0
                                ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                                : tableRows.length === 1
                                  ? 'cursor-not-allowed border-gray-200 text-gray-300'
                                  : 'border-red-100 text-red-600 hover:border-red-300 hover:text-red-700'
                            }`}
                          >
                            <XCircle className="h-4 w-4" />
                            {index === 0 ? 'Clear' : 'Close'}
                          </button>
                        )}
                      </div>

                      <div className="space-y-3">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Date
                          </label>
                          <input
                            type="date"
                            value={row.date}
                            onChange={(e) =>
                              handleRowChange(index, 'date', e.target.value)
                            }
                            readOnly={isDataFromAPI}
                            className={`h-11 w-full rounded-2xl border bg-gray-50 px-4 text-sm focus:outline-none focus:ring-2 ${
                              errors.rows[index]?.date
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.date && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].date}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Doc# Number
                          </label>
                          <Input
                            type="text"
                            value={row.docNumber}
                            onChange={(e) =>
                              handleRowChange(
                                index,
                                'docNumber',
                                e.target.value
                              )
                            }
                            readOnly={isDataFromAPI}
                            placeholder="Enter doc number"
                            className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                              errors.rows[index]?.docNumber
                                ? 'border-red-500 focus:ring-red-500'
                                : 'border-gray-200 focus:ring-green-500'
                            } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                          />
                          {errors.rows[index]?.docNumber && (
                            <p className="mt-1 text-xs text-red-600">
                              {errors.rows[index].docNumber}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Total Amount
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.totalAmount}
                              onChange={(e) =>
                                handleRowChange(
                                  index,
                                  'totalAmount',
                                  e.target.value
                                )
                              }
                              readOnly={isDataFromAPI}
                              placeholder="0.00"
                              className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                errors.rows[index]?.totalAmount
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 focus:ring-green-500'
                              } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                            />
                            {errors.rows[index]?.totalAmount && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].totalAmount}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Tax Amount
                            </label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={row.taxAmount}
                              onChange={(e) =>
                                handleRowChange(index, 'taxAmount', e.target.value)
                              }
                              readOnly={isDataFromAPI}
                              placeholder="0.00"
                              className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none ${
                                errors.rows[index]?.taxAmount
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 focus:ring-green-500'
                              } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                            />
                            {errors.rows[index]?.taxAmount && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].taxAmount}
                              </p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            View Doc
                          </label>
                          <button
                            onClick={() =>
                              handleViewDoc(row.docUrl, row.docNumber)
                            }
                            disabled={!row.docUrl}
                            className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Eye className="w-4 h-4" />
                            View Document
                          </button>
                        </div>

                        <div className="grid grid-cols-1 gap-3">
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Vendor PAN
                            </label>
                            <Input
                              type="text"
                              value={row.vendorPan}
                              onChange={(e) =>
                                handleRowChange(index, 'vendorPan', e.target.value)
                              }
                              readOnly={isDataFromAPI}
                              placeholder="Enter Vendor PAN"
                              maxLength={10}
                              className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                                errors.rows[index]?.vendorPan
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 focus:ring-green-500'
                              } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                            />
                            {errors.rows[index]?.vendorPan && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].vendorPan}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Vendor GSTIN
                            </label>
                            <Input
                              type="text"
                              value={row.vendorGstin}
                              onChange={(e) =>
                                handleRowChange(index, 'vendorGstin', e.target.value)
                              }
                              readOnly={isDataFromAPI}
                              placeholder="Enter Vendor GSTIN"
                              maxLength={15}
                              className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                                errors.rows[index]?.vendorGstin
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 focus:ring-green-500'
                              } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                            />
                            {errors.rows[index]?.vendorGstin && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].vendorGstin}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Customer PAN
                            </label>
                            <Input
                              type="text"
                              value={row.customerPan}
                              onChange={(e) =>
                                handleRowChange(index, 'customerPan', e.target.value)
                              }
                              readOnly={isDataFromAPI}
                              placeholder="Enter Customer PAN"
                              maxLength={10}
                              className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                                errors.rows[index]?.customerPan
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 focus:ring-green-500'
                              } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                            />
                            {errors.rows[index]?.customerPan && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].customerPan}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-gray-500">
                              Customer GSTIN
                            </label>
                            <Input
                              type="text"
                              value={row.customerGstin}
                              onChange={(e) =>
                                handleRowChange(index, 'customerGstin', e.target.value)
                              }
                              readOnly={isDataFromAPI}
                              placeholder="Enter Customer GSTIN"
                              maxLength={15}
                              className={`h-11 rounded-2xl border bg-gray-50 px-4 text-sm shadow-none uppercase ${
                                errors.rows[index]?.customerGstin
                                  ? 'border-red-500 focus:ring-red-500'
                                  : 'border-gray-200 focus:ring-green-500'
                              } ${isDataFromAPI ? 'cursor-not-allowed opacity-75' : ''}`}
                            />
                            {errors.rows[index]?.customerGstin && (
                              <p className="mt-1 text-xs text-red-600">
                                {errors.rows[index].customerGstin}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                   {!isDataFromAPI && (
                     <div className="flex justify-center">
                       <button
                         type="button"
                         onClick={handleAddRow}
                         className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 bg-white px-5 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
                       >
                         <PlusCircle className="h-4 w-4" />
                         Add Row
                       </button>
                     </div>
                   )}
                </div>
              )}
            </div>
          </div>

          {/* Submit Button - Only show when data is NOT from API */}
          {!isDataFromAPI && (
            <div className="flex mt-8">
              <PrimaryButton
                type="submit"
                disabled={isLoading}
                className="w-auto min-w-[200px]"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5 text-white mr-2"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </PrimaryButton>
            </div>
          )}

          {/* Success/Error Messages */}
          {(success || error) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-4 p-4 rounded-lg ${
                success
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}
            >
              {success || error}
            </motion.div>
          )}
        </motion.div>
        </form>
      </div>
    </div>
  );
};

export default TaxOverView;

